import { isBlobNode, type BlobRef } from "@ide/shared";
import type { FileHandle, ProjectSession } from "@/collab/session";

/**
 * Observa todos os arquivos de texto do projeto aberto: devolve o conteúdo atual e avisa cada
 * mudança (edição, criação, exclusão, renomear) — sua ou de quem programa junto. Base da cópia
 * local na máquina.
 */
export interface FileChange {
  path: string;
  content: string | null;
}

export const WATCH_LIMIT = 5_000;

/**
 * Arquivo binário no lugar do texto (o mesmo marcador do app desktop): "\0kompil-blob:<sha>:<bytes>".
 * A pasta do computador recebe o marcador e pede os bytes; do disco, o marcador indica o que enviar.
 */
const BLOB_MARK = "\u0000kompil-blob:";
export const blobMarker = (ref: BlobRef) => `${BLOB_MARK}${ref.sha256}:${ref.size}`;
export function parseBlobMarker(value: string | null | undefined): BlobRef | null {
  if (!value?.startsWith(BLOB_MARK)) return null;
  const [sha256, size] = value.slice(BLOB_MARK.length).split(":");
  return sha256 && /^[0-9a-f]{64}$/.test(sha256) && size !== undefined
    ? { sha256, size: Number(size) }
    : null;
}

export class ProjectWatch {
  private handles = new Map<string, { handle: FileHandle; path: string; off: () => void }>();
  /** Arquivos binários vistos (id → caminho e marcador): mudou o sha, avisa. */
  private blobs = new Map<string, { path: string; marker: string }>();
  private pending = new Map<string, string | null>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private offTree: (() => void) | null = null;
  private live = false;

  constructor(
    private readonly session: ProjectSession,
    private readonly onChanges: (changes: FileChange[]) => void,
  ) {}

  private queue(path: string, content: string | null) {
    if (!this.live) return;
    this.pending.set(path, content);
    this.timer ??= setTimeout(() => this.flush(), 250);
  }

  /** Envia já o que estiver pendente (antes de rodar algo no terminal). */
  flush() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (!this.pending.size) return;
    const changes = [...this.pending].map(([path, content]) => ({ path, content }));
    this.pending.clear();
    this.onChanges(changes);
  }

  /** Abre os arquivos que faltam (e solta os que saíram); devolve o conteúdo de cada um. */
  /** Arquivos que não carregaram na última conferência (o conteúdo é desconhecido). */
  unloaded: string[] = [];

  async collect(): Promise<Record<string, string>> {
    const tree = this.session.tree();
    const unloaded: string[] = [];
    const wanted = new Map<string, string>();
    const files: Record<string, string> = {};
    const blobs = new Map<string, { path: string; marker: string }>();
    for (const [id, path] of tree.paths) {
      const node = tree.nodes.get(id);
      if (node?.type !== "file") continue;
      if (isBlobNode(node)) {
        const marker = blobMarker(node.blob);
        blobs.set(id, { path, marker });
        files[path] = marker;
        continue;
      }
      if (wanted.size >= WATCH_LIMIT) break;
      wanted.set(id, path);
    }
    for (const [id, b] of blobs) {
      const old = this.blobs.get(id);
      if (old?.path !== b.path || old.marker !== b.marker) this.queue(b.path, b.marker);
    }
    for (const [id, old] of this.blobs) {
      const now = blobs.get(id);
      // Saiu (apagado ou renomeado) e o caminho antigo não é de outro arquivo agora.
      if (now?.path !== old.path && !(old.path in files) && ![...wanted.values()].includes(old.path))
        this.queue(old.path, null);
    }
    this.blobs = blobs;
    for (const [id, entry] of this.handles) {
      if (wanted.get(id) === entry.path) continue;
      entry.off();
      entry.handle.release();
      this.handles.delete(id);
      // Renomear = apagar o caminho antigo; o novo chega abaixo (virou binário: o marcador já foi).
      if (!(entry.path in files)) this.queue(entry.path, null);
    }
    await Promise.all(
      [...wanted].map(async ([id, path]) => {
        let entry = this.handles.get(id);
        if (!entry) {
          const handle = this.session.openFile(id);
          const observer = () => this.queue(path, handle.text.toString());
          entry = { handle, path, off: () => handle.text.unobserve(observer) };
          this.handles.set(id, entry);
          // Não carregou: fica fora (texto vazio aqui apagaria o arquivo na pasta).
          if (!(await this.session.loadedOk(handle))) {
            entry.off();
            handle.release();
            this.handles.delete(id);
            unloaded.push(path);
            return;
          }
          handle.text.observe(observer);
          this.queue(path, handle.text.toString());
        }
        files[path] = entry.handle.text.toString();
      }),
    );
    this.unloaded = unloaded;
    return files;
  }

  /** Começa a avisar mudanças (depois da primeira sincronização). */
  start() {
    this.live = true;
    this.pending.clear();
    // Muitas mudanças na árvore de uma vez (pasta aberta no projeto): confere uma vez só.
    let timer: ReturnType<typeof setTimeout> | null = null;
    this.offTree ??= this.session.on("tree", () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        if (this.live) void this.collect();
      }, 150);
    });
  }

  stop() {
    this.live = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.offTree?.();
    this.offTree = null;
    for (const e of this.handles.values()) {
      e.off();
      e.handle.release();
    }
    this.handles.clear();
    this.blobs.clear();
  }
}
